import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useToggle } from 'hooks';
import { ErrorBoundary, RouteFallback } from 'components';
import { Navbar, Sidebar, MobileSidebar, Footer } from 'layouts/components';
import { NAV_BY_ROLE } from 'layouts/components/navConfig';
import MobileTabBar from 'layouts/components/MobileTabBar';
import { ROLES } from 'config/roles';

// Roles whose rail is short enough for a phone tab bar (five items or fewer).
const TAB_BAR_ROLES = new Set([ROLES.STUDENT]);

/**
 * DashboardShell — the shared authenticated chrome behind every role area.
 * Composes the sticky top bar, the fixed collapsible glass sidebar (desktop),
 * the responsive drawer (mobile), and a scrollable content region that renders
 * the active route via <Outlet>.
 *
 * Role-specific layouts (AdminLayout / MentorLayout / StudentLayout) wrap this
 * with their `role`, which selects the nav set and workspace labelling. Sidebar
 * collapse state persists across reloads; the mobile drawer auto-closes on
 * navigation.
 *
 * @param {('student'|'mentor'|'admin')} role  Drives navigation + labels.
 */
// v2: default flipped to collapsed (icon rail) — key bumped so the old
// auto-persisted '0' from the expanded-by-default era doesn't pin users open.
const COLLAPSE_KEY = 'lx.sidebar.collapsed.v2';

export default function DashboardShell({ role }) {
  const items = NAV_BY_ROLE[role] || [];
  const tabBar = TAB_BAR_ROLES.has(role);

  // Default to the slim icon rail (website feel) — content owns the width.
  // An explicit user toggle ('0' = expanded) still wins and persists.
  // Students default to the open rail with labels (their skin follows the Teen
  // mockup, and their list is long enough that icons alone don't say much).
  const student = role === ROLES.STUDENT;
  // Mentors share the student's quieter rail — a pale active pill and the
  // profile card — so the colour on their pages is the data's, not the nav's.
  const softRail = student || role === ROLES.MENTOR;
  const [collapsed, toggleCollapsed] = useToggle(() => {
    if (typeof window === 'undefined') return !student;
    const saved = window.localStorage.getItem(COLLAPSE_KEY);
    return student ? saved === '1' : saved !== '0';
  });
  const [mobileOpen, , openMobile, closeMobile] = useToggle(false);

  // Persist the collapsed preference.
  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close the drawer whenever the route changes.
  const { pathname } = useLocation();
  useEffect(() => {
    closeMobile();
  }, [pathname, closeMobile]);

  return (
    <div className={`flex min-h-screen flex-col ${student ? 'lx-student' : ''}`}>
      <Navbar onOpenMobile={openMobile} />

      <div className="flex flex-1">
        <Sidebar
          items={items}
          role={role}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          tone={softRail ? 'soft' : 'solid'}
          showUser={softRail}
        />

        <MobileSidebar open={mobileOpen} onClose={closeMobile} items={items} role={role} />

        {/* With a phone tab bar, reserve its height so the footer isn't hidden under it. */}
        <div className={`flex min-w-0 flex-1 flex-col bg-canvas ${tabBar ? 'pb-20 md:pb-0' : ''}`}>
          {/* lx-shell-main: size container for full-bleed bands — see index.css. */}
          <main className="lx-shell-main flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
            {/* No entrance animation — pages render immediately, like a website. */}
            <div className="mx-auto w-full max-w-content">
              {/* Per-page boundary: a crash in one page is contained within the
                  chrome, and the pathname key remounts (recovers) on navigation.
                  Suspense renders a content skeleton while the lazy chunk loads. */}
              <ErrorBoundary key={pathname} variant="inline">
                <Suspense fallback={<RouteFallback />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
          <Footer />
        </div>
      </div>

      {tabBar && <MobileTabBar items={items} />}
    </div>
  );
}
